import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup } from 'reactstrap';
import styled from 'styled-components';

import BackButton from '../../../custom/BackButton';
import ActionIcon from '../../../custom/ActionIcon';
import { createdUpdatedInfo, getGuid, validateAwb as validateMawb } from '../../../../utils';
import { Uld, UldArray } from './CreateTransferExport';
import { Input } from 'reactstrap';
import _ from 'lodash';
import { IAirport, IFFM } from '../../../../globals/interfaces';
import MawbInput from '../../../custom/MawbInput';
import { Button } from 'reactstrap';
import SpecialHandlingCodes from '../../../custom/SpecialHandlingCodes';
import { useAppContext } from '../../../../context';
import useSelection from '../../../../customHooks/useSelection';
import SelectAirport from '../../../custom/SelectAirport';
import { IFFMSchema, IFFMSchemaType } from './postDataSchema';
import moment from 'moment';
import useUser from '../../../../customHooks/useUser';
import { formatFlightNumber } from './utils';
import useShcs from '../../../../customHooks/useShcs';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    setUldArray: React.Dispatch<React.SetStateAction<UldArray>>;
    selectedUld: Uld | null; // The ULD in which we are adding/updating AWBs
    selectedAwb: Partial<IFFM> | null; // The AWB to edit (null if creating new)
    airportCodes: Array<IAirport>;
    airportCodesMap: Record<string, string>;
    now: Date;
}

export default function ModalAwb({
    modal,
    setModal,
    setUldArray,
    selectedUld,
    selectedAwb,
    airportCodes,
    airportCodesMap,
    now
}: Props) {

    const shcs = useShcs();
    const { user: { s_email } } = useUser();
    const selectedUldKey = _.get(selectedUld, 'key', '');
    const update = Boolean(selectedAwb);
    const [awb, setAwb] = useState<IFFMSchemaType>({});

    const { selected, setSelected, selectedString } = useSelection(awb.s_special_handling_code);
    const [error, setError] = useState("");
    const [valid, setValid] = useState(false);

    useEffect(() => {
        if (!selectedUld) return;

        if (selectedAwb) {
            setAwb(selectedAwb);
        } else {
            setAwb({
                s_uld: selectedUld.uld,
                s_mawb: "",
                s_origin: "",
                s_destination: "",
                i_actual_piece_count: 0,
                f_weight: 0,
                s_commodity: "",
                s_special_handling_code: "",
                s_created_by: s_email,
                t_created: now,
                s_modified_by: s_email,
                t_modified: now,
                s_guid: getGuid()
            });
        }
        setError("");
    }, [selectedUld, selectedAwb, modal]);

    useEffect(() => {
        setAwb((prev) => ({ ...prev, s_special_handling_code: selectedString }));
    }, [selectedString]);

    useEffect(() => {
        const result = IFFMSchema.safeParse(awb);
        // try {
        //     console.log(IFFMSchema.parse(awb));
        // } catch (err) {
        //     console.log(err);
        //     console.log(awb);
        // }
        setValid(result.success); // ✅ Update state based on validation result
    }, [awb]);

    const handleSaveAwb = () => {
        if (!awb.s_mawb.trim()) {
            setError("MAWB cannot be empty");
            return;
        }

        setUldArray((prev) => {
            const copy = [...prev];
            const uldIndex = copy.findIndex((uld) => uld.key === selectedUldKey);

            if (uldIndex === -1) return prev; // If ULD is not found, do nothing

            if (update) {
                // ✅ Update existing AWB
                const awbIndex = copy[uldIndex].data.findIndex((item) => item.s_mawb === selectedAwb?.s_mawb);
                if (awbIndex !== -1) {
                    copy[uldIndex].data[awbIndex] = awb;
                }
            } else {
                // ✅ Add new AWB
                copy[uldIndex].data.push({ ...awb, s_guid: getGuid() });
            }

            return copy;
        });

        toggle();
    };

    const handleDeleteAwb = () => {
        setUldArray((prev) => {
            const copy = [...prev];
            const uldIndex = copy.findIndex((uld) => uld.key === selectedUldKey);

            if (uldIndex !== -1) {
                copy[uldIndex].data = copy[uldIndex].data.filter(item => item.s_mawb !== selectedAwb?.s_mawb);
            }

            return copy;
        });
        toggle();
    }

    const toggle = () => setModal(!modal);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={"d-flex"}>
                <h4>{update ? "Update" : "Create"} AWB</h4>
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>MAWB</Label>
                    <MawbInput
                        value={awb.s_mawb}
                        onChange={(value) => setAwb((prev) => ({ ...prev, s_mawb: value }))}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Origin</Label>
                    <SelectAirport
                        airports={airportCodes}
                        airportCodesMap={airportCodesMap}
                        code={awb.s_origin}
                        setCode={(value) =>
                            setAwb((prev) => ({ ...prev, s_origin: value }))
                        }
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Destination</Label>
                    <SelectAirport
                        airports={airportCodes}
                        airportCodesMap={airportCodesMap}
                        code={awb.s_destination}
                        setCode={(value) =>
                            setAwb((prev) => ({ ...prev, s_destination: value }))
                        }
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Pieces</Label>
                    <Input
                        type="number"
                        value={awb.i_actual_piece_count}
                        onChange={(e) => setAwb((prev) => ({ ...prev, i_actual_piece_count: Number(e.target.value) }))}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Weight (kilos)</Label>
                    <Input
                        type="number"
                        value={awb.f_weight}
                        onChange={(e) => setAwb((prev) => ({ ...prev, f_weight: Number(e.target.value) }))}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Commodity</Label>
                    <Input
                        type="text"
                        value={awb.s_commodity}
                        maxLength={14} 
                        onChange={(e) => setAwb((prev) => ({ ...prev, s_commodity: e.target.value }))}
                    />
                </FormGroup>

                <FormGroup>
                    <Label>SHC (Special Handling Code)</Label>
                    <SpecialHandlingCodes
                        shcs={shcs}
                        selectedShcs={selected}
                        setSelectedShcs={setSelected}
                    />
                </FormGroup>

                {error && <p style={{ color: "red" }}>{error}</p>}
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <FooterButtonsContainer update={update}>
                        {
                            update && (
                                <ActionIcon
                                    type={'delete'}
                                    onClick={handleDeleteAwb}
                                />
                            )
                        }
                        <ActionIcon
                            type={'save'}
                            disabled={!valid}
                            onClick={handleSaveAwb}
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const Flex = styled.div`
    display: flex;
    gap: 10px;
`;

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;